import { Component, computed, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslatePipe } from '../../../pipes/translate.pipe';
import { InvestmentGoal, ObjectiveDetails } from '../../../domain/lti/lti.domain';
import { CurrencyPipe } from '../../../pipes/currency.pipe';
import { ChartComponent } from '../../../components/charts/chart.component';
import { LocaleService } from '../../../services/locale/locale.service';
import { ProjectionUnitCode } from '../../../domain/lti/lti.enum';
import { Breakpoint, BreakpointState, MediaQueryService } from '../../../services/media-query.service';
import { DatePipe } from '../../../pipes/date.pipe';
import type { AxisOptions } from 'highcharts';
import type Highcharts from 'highcharts/highstock';

@Component({
  selector: 'td-wb-lti-projection-chart',
  template: '<div [attr.id]="renderTo"><td-wb-chart [options]="options" /></div>',
  styleUrls: ['projection-chart.component.scss'],
  imports: [
    ChartComponent
  ],
  providers: [TranslatePipe]
})
export class ProjectionChartComponent {
  private readonly mediaQueryService = inject(MediaQueryService);
  private readonly currencyPipe = inject(CurrencyPipe);
  private readonly datePipe = inject(DatePipe);
  private readonly translatePipe = inject(TranslatePipe);
  private readonly localeService = inject(LocaleService);

  readonly goal = input<InvestmentGoal>(undefined);
  readonly showProjectionAmount = input<boolean>(undefined);
  renderTo: string = 'td-wb-lti-projection-chart__container';

  private readonly currentLang: 'en' | 'fr' = this.localeService.getLanguage();
  private readonly breakpointState = toSignal(
    this.mediaQueryService.observe(Breakpoint.SM, Breakpoint.MD, Breakpoint.LG, Breakpoint.GT_LG),
    { initialValue: undefined }
  );

  private readonly optionsSignal = computed<Highcharts.Options | undefined>(() => {
    const goal = this.goal();
    const breakpointState = this.breakpointState();
    if (goal === undefined || breakpointState === undefined || goal.investmentObjective === undefined) {
      return undefined;
    }

    const chartData = this.getData(goal);
    if (chartData === undefined) {
      return undefined;
    }

    return this.buildChartOptions(
      chartData,
      breakpointState,
      goal.targetAmount,
      goal.projectedAmount,
      goal.investmentObjective.projectionUnit
    );
  });

  get options(): Highcharts.Options | undefined {
    return this.optionsSignal();
  }

  /**
   * This function creates the actual array of data that the chart needs.
   * It creates a UTC date out of the string format date that we receive from the backend
   *
   * @param investmentGoal All of the health check data
   * @returns The data array that the chart plots
   */
  private getData(investmentGoal: InvestmentGoal): Array<Record<string, unknown>> | undefined {
    if (investmentGoal.investmentObjective !== undefined) {
      const objectiveDetails = investmentGoal.investmentObjective.objectiveDetails;
      const data = [];
      objectiveDetails.forEach((detail: ObjectiveDetails): void => {
        const date = detail.asOfDate;
        const dateTime = date.getTime();
        data.push({ x: dateTime, y: detail.projectedAmount, age: detail.age });
      });
      return data;
    }
  }

  private buildChartOptions(
    chartData: Array<Record<string, unknown>>,
    breakpointState: BreakpointState,
    targetAmount: number,
    projectedAmount: number,
    projectionUnit: ProjectionUnitCode
  ): Highcharts.Options {
    const plotLabelForGoalsText = this.getPlotLabelForGoalsText(targetAmount);
    const plotLabelForProjectedText = this.getPlotLabelForProjectedText(projectedAmount);

    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;
    const options: Highcharts.Options = {
      accessibility: {
        enabled: true,
        screenReaderSection: {
          beforeChartFormat: ''
        }
      },
      lang: {
        accessibility: {
          chartContainerLabel: this.translatePipe.transform('tdWb.lti.chart.TITLE')
        }
      },
      chart: {
        backgroundColor: '#ffffff',
        type: 'area',
        renderTo: this.renderTo
      },
      title: {
        text: undefined
      },
      xAxis: {
        type: 'datetime',
        labels: {
          formatter(): string {
            // Safely access the value in Highcharts 12.3.0
            const dateValue = this.value as unknown as Date;
            if (!dateValue) {
              return '';
            }

            switch (projectionUnit) {
              case ProjectionUnitCode.YEARLY:
              case ProjectionUnitCode.SIX_MONTHLY:
                return self.datePipe.transform(dateValue, { format: 'YEAR' });
              case ProjectionUnitCode.QUARTERLY:
              case ProjectionUnitCode.MONTHLY:
                return self.datePipe.transform(dateValue, { format: self.currentLang === 'en' ? 'MONTH_YEAR_ABBR' : 'YEAR_MONTH' });
              case ProjectionUnitCode.WEEKLY:
              case ProjectionUnitCode.DAILY:
                return self.datePipe.transform(dateValue, { format: 'DATE_MONTH' });
              default:
                return '';
            }
          },
          style: {
            fontSize: '12px'
          }
        }
      },
      yAxis: {
        title: {
          text: ''
        },
        labels: {
          formatter(): string {
            // Safely access the value in Highcharts 12.3.0
            const value = this.value !== undefined ? this.value as number : 0;
            return self.currencyPipe.transform(value, { shorten: true, format: 'c?1.0-0' });
          },
          style: {
            fontSize: '12px'
          }
        },
        min: 0,
        minRange: targetAmount,
        plotLines: [{
          color: '#4b94b9', // Color value
          dashStyle: 'LongDash', // Style of the plot line. Default to solid
          value: targetAmount, // Value of where the line will appear
          width: 2, // Width of the line,
          zIndex: 4,
          label: {
            text: plotLabelForGoalsText,
            align: 'left',
            style: {
              color: '#31728c',
              fontSize: '13px',
              fontFamily: 'robotomedium, Arial, sans-serif'
            }
          }
        }, {
          color: '#a65207', // Color value
          dashStyle: 'LongDash', // Style of the plot line. Default to solid
          value: this.showProjectionAmount() ? projectedAmount : undefined, // Value of where the line will appear
          width: 2, // Width of the line,
          zIndex: 4,
          label: {
            text: plotLabelForProjectedText,
            align: 'right',
            x: -10,
            style: {
              color: '#a65207',
              fontSize: '13px',
              fontFamily: 'robotomedium, Arial, sans-serif'
            }
          }
        }]
      },
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.90)',
        borderColor: '#96cb95',
        style: {
          fontSize: '14px',
          padding: '8px'
        },
        formatter(): string {
          // Safely access the point data in Highcharts 12.3.0
          const pointData = this.points?.[0] || this;
          const yValue = pointData.y !== undefined ? pointData.y : 0;
          const amount = self.currencyPipe.transform(yValue, { format: 'c0' });
          return '<b>' + amount + '</b>';
        }
      },
      plotOptions: {
        line: {
          color: '#008a00'
        },
        area: {
          color: '#008a00',
          fillColor: {
            linearGradient: { x1: 0, x2: 0, y1: 0, y2: 1 },
            stops: [
              [0.6, 'rgba(242,249,242,1)'],
              [1, 'rgba(255,255,255,0)']
            ]
          },
          lineWidth: 2,
          shadow: false,
          threshold: null,
          marker: {
            enabled: false,
            symbol: 'circle',
            radius: 2,
            states: {
              hover: {
                enabled: true
              }
            }
          }
        }
      },
      legend: {
        enabled: false
      },
      time: ({ useUTC: false } as unknown),
      series: [{
        data: chartData,
        type: 'area'
      }]
    };

    this.addPlotLineLabelYShift(options, plotLabelForGoalsText, plotLabelForProjectedText);
    this.updateChartSizeOption(options, breakpointState);

    return options;
  }

  /**
   * In case plot line label text is multi-lined:
   * (1) Add Plot line label shift so it would be above plot line
   * (2) Add additional padding so plot legend doesn't go beyond the chart
   */
  private addPlotLineLabelYShift(options: Highcharts.Options,
                                 plotLabelForGoalsText: string,
                                 plotLabelForProjectedText: string): void {
    const lineBreaksInGoalLabel = this.countNumberOfLineBreakes(plotLabelForGoalsText);
    const lineBreaksInProjectionLabel = this.countNumberOfLineBreakes(plotLabelForProjectedText);
    const singleLineShift = 19;
    if (lineBreaksInGoalLabel > 0) {
      (options.yAxis as AxisOptions).plotLines[0].label.y = -lineBreaksInGoalLabel * singleLineShift;
    }
    if (lineBreaksInProjectionLabel > 0) {
      (options.yAxis as AxisOptions).plotLines[1].label.y = -lineBreaksInProjectionLabel * singleLineShift;
    }
    // calculate maxPadding to insure the plot legend doesn't go beyond the chart
    const max = Math.max(lineBreaksInGoalLabel, lineBreaksInProjectionLabel);
    if (max > 0) {
      (options.yAxis as AxisOptions).maxPadding = max === 1 ? 0.08 : max / 10; // maxPadding is % of chart height
    }
  }

  private updateChartSizeOption(options: Highcharts.Options, breakpointState: BreakpointState): void {
    // This is to fix issue with chart being too small in Tablet portrait view WB-21453.
    if (breakpointState.isBreakpointMatch(Breakpoint.SM)) {
      options.chart.height = '66%';
    } else if (breakpointState.isBreakpointMatch(Breakpoint.MD)) {
      options.chart.height = '410';
    } else if (breakpointState.isBreakpointMatch(Breakpoint.LG)) {
      options.chart.height = '490';
    } else if (breakpointState.isBreakpointMatch(Breakpoint.GT_LG)) {
      options.chart.height = '480';
    }
  }

  private getPlotLabelForGoalsText(targetAmount: number): string {
    return this.translatePipe.transform('tdWb.lti.chart.GOAL_TO_HAVE') +
      this.currencyPipe.transform(targetAmount, { format: 'c0' });
  }

  private getPlotLabelForProjectedText(projectedAmount: number): string {
    return this.translatePipe.transform('tdWb.lti.chart.PROJECTED_TO_HAVE') +
      this.currencyPipe.transform(projectedAmount, { format: 'c0' });
  }

  private countNumberOfLineBreakes(str: string): number {
    const result = str.match(/<br>/g);
    return result === null ? 0 : result.length;
  }
}

